/**
 * Time formatting for the Schedule Timeline (#946).
 *
 * Two rules this module exists to enforce, both taken from the app rather
 * than invented here:
 *
 * 1. Connector/execution surfaces render 12-hour `h:mm a` (ExecutionDetail,
 *    ExecutionList, SchedulePreview). The 24-hour `HH:mm` form in this app
 *    belongs to Monitors/Schemas/Datasets and must not appear here.
 * 2. A SPECIFIC INSTANT takes the Intl-derived zone abbreviation (EDT/EST); a
 *    RECURRING RULE takes the fixed short label (ET), because no single
 *    abbreviation is correct year-round. The label table and that rule now
 *    live in @/lib/time/zoneLabels (#1032).
 */
import { shortZoneLabel } from '@/lib/time/zoneLabels';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function partsIn(ms: number, tz: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(new Date(ms));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    weekday: get('weekday'),
    month: get('month'),
    day: get('day'),
    hour: Number(get('hour')),
    minute: Number(get('minute')),
    dayPeriod: get('dayPeriod'),
  };
}

/** The Intl-derived abbreviation for this instant — EDT or EST, not ET. */
export function instantZoneAbbr(ms: number, tz: string): string {
  return (
    new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(new Date(ms))
      .find((p) => p.type === 'timeZoneName')?.value ?? shortZoneLabel(tz)
  );
}

/** 12-hour clock. `compact` drops `:00` on the hour ("3 AM"). */
export function formatClock(ms: number, tz: string, opts: { compact?: boolean } = {}): string {
  const { hour, minute, dayPeriod } = partsIn(ms, tz);
  return opts.compact && minute === 0
    ? `${hour} ${dayPeriod}`
    : `${hour}:${String(minute).padStart(2, '0')} ${dayPeriod}`;
}

/** A full timestamp: "Wed Jul 22, 3:00 AM EDT". */
export function formatInstant(ms: number, tz: string): string {
  const { weekday, month, day } = partsIn(ms, tz);
  return `${weekday} ${month} ${day}, ${formatClock(ms, tz)} ${instantZoneAbbr(ms, tz)}`;
}

/** A day boundary label: "Wed 22", in the display zone. */
export function formatDayLabel(ms: number, tz: string): string {
  const { weekday, day } = partsIn(ms, tz);
  return `${weekday} ${day}`;
}

/**
 * Offset in ms between UTC and `tz` at this instant.
 *
 * The axis grid aligns to this so day boundaries land on the DISPLAY zone's
 * midnight. Aligning to UTC would put a 24h step on 8 PM ET every day and no
 * tick would ever be a day boundary; aligning to the browser's own offset
 * would be wrong for any viewer outside the display zone.
 */
export function zoneOffsetMs(ms: number, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(ms));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asIfUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second'),
  );
  return asIfUtc - Math.floor(ms / 1000) * 1000;
}

export { DAYS, MONTHS };
